import {
  RouteCandidateChoiceKindEnum,
  RouteFieldTesterSuggestionDTO,
  RouteOtherFieldTesterDTO,
  RoutePackageDTO,
  RoutePlanCandidateRejectionReasonEnum,
} from 'api/model';

/**
 * Varför rank 1 valdes, och vilka andra som fanns (ruttfeedback 2026-10-28, Feedbackknapp-1:
 * "Den har valt en fälttestare med kort tillgänglighet"). Trafikledaren såg vem som valdes men
 * inte varför, och kunde därför inte bedöma valet rättvist.
 *
 * Paketnumret skrivs olika i vyerna (planeringen räknar från 1, ruttprovet visar indexet), så
 * vyn skickar in sitt eget format.
 */
export type PackageLabel = (packageIndex: number) => string;

const formatKm = (km: number): string =>
  `${km.toLocaleString('sv-SE', { maximumFractionDigits: km < 10 ? 1 : 0 })} km`;

/** "1 h 30 min", "45 min". Negativt = över fönstret. */
const formatMinutes = (minutes: number): string => {
  const abs = Math.abs(minutes);
  const hours = Math.floor(abs / 60);
  const rest = abs % 60;
  if (hours === 0) return `${rest} min`;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
};

/** Hur mycket dagen kan förlängas med henne, eller hur långt den redan går över fönstret. */
export const describeSpareTime = (
  spareMinutesAfterDay: number | null | undefined
): string | null => {
  if (spareMinutesAfterDay == null) return null;
  if (spareMinutesAfterDay < 0) {
    return `${formatMinutes(spareMinutesAfterDay)} över tillgängligheten`;
  }
  return `${formatMinutes(spareMinutesAfterDay)} kvar efter dagen`;
};

/**
 * Det avgörande skälet till rank 1, som en mening. Null när servern inte förklarat valet (äldre
 * förslag, skiftestransport, manuellt vald).
 */
export const describeCandidateChoice = (
  pkg: RoutePackageDTO,
  packageLabel: PackageLabel
): string | null => {
  const choice = pkg.candidateChoice;
  const top = pkg.candidates.find((c) => c.rank === 1);
  if (!choice || !top) return null;

  const topKm = top.proximityKm != null ? formatKm(top.proximityKm) : null;
  const alt = choice.alternativeName ?? 'en annan fälttestare';
  const altKm =
    choice.alternativeProximityKm != null
      ? formatKm(choice.alternativeProximityKm)
      : null;
  const versus = altKm && topKm ? ` (${altKm} mot ${topKm})` : '';

  switch (choice.kind) {
    case RouteCandidateChoiceKindEnum.OnlyCandidate:
      return 'Ingen annan fälttestare kunde ta paketet.';
    case RouteCandidateChoiceKindEnum.Closest:
      // Samma ankare: avståndet avgjorde inte, fördelningen och belastningen gjorde det.
      if (
        top.proximityKm != null &&
        choice.alternativeProximityKm != null &&
        Math.abs(choice.alternativeProximityKm - top.proximityKm) < 0.5
      ) {
        return `Lika nära som ${alt} (${topKm} till första upphämtningen). Fördelningen över alla paket och belastningen avgjorde.`;
      }
      return `Närmast: ${topKm} till första upphämtningen. Näst närmast: ${alt}${
        altKm ? `, ${altKm}` : ''
      }.`;
    case RouteCandidateChoiceKindEnum.CloserNeededElsewhere:
      return `${alt} låg närmare${versus} men är förstaval i ${
        choice.alternativePackageIndex != null
          ? packageLabel(choice.alternativePackageIndex)
          : 'ett annat paket'
      }. Fördelningen ger minst tomkörning totalt så.`;
    case RouteCandidateChoiceKindEnum.GlobalAssignment:
      // Inget enskilt skäl går att peka ut: matrisen vägde även schema, straff och hemresa.
      return `${alt} låg närmare${versus}, men fördelningen över alla paket valde ${top.fullName}.`;
    case RouteCandidateChoiceKindEnum.FullTimeFirst:
      return `Heltidare först: ${top.fullName} är heltidsanställd och dagen ryms i schemat. ${alt} låg närmare${versus}.`;
    case RouteCandidateChoiceKindEnum.CloserIsEmergencyOnly:
      return `${alt} låg närmare${versus} men har behörigheten bara i nödfall.`;
    case RouteCandidateChoiceKindEnum.CloserFreedLater:
      return `${alt} låg närmare${versus} och är ledig nu. Hen var förstaval i ett annat paket när det här fördelades, men det paketet planerades om efteråt. Överväg att byta.`;
    case RouteCandidateChoiceKindEnum.ScheduleDelay:
      return `Ingen hann i tid. ${top.fullName} kan med en senare start.`;
    default:
      return null;
  }
};

/** Var en annan kandidat (rank 2–3) står i förslaget. */
export const describeCandidateStatus = (
  candidate: RouteFieldTesterSuggestionDTO,
  packageLabel: PackageLabel
): string | null =>
  candidate.rankOneInPackageIndex != null
    ? `förstaval i ${packageLabel(candidate.rankOneInPackageIndex)}`
    : null;

/** Varför en ledig fälttestare inte är kandidat. */
export const describeOtherStatus = (
  other: RouteOtherFieldTesterDTO,
  packageLabel: PackageLabel
): string => {
  if (other.rankOneInPackageIndex != null) {
    return `förstaval i ${packageLabel(other.rankOneInPackageIndex)}`;
  }
  switch (other.reason) {
    case RoutePlanCandidateRejectionReasonEnum.AlreadyUsedInRun:
      // Upptagen när paketet fördelades, men inte förstaval någonstans nu.
      return 'ledig nu – frigjordes efter fördelningen';
    case RoutePlanCandidateRejectionReasonEnum.NotAvailable:
      return 'schemat räcker inte';
    case RoutePlanCandidateRejectionReasonEnum.DrivingPolicyBlocked:
      return 'körtidsreglerna (EU-561)';
    case RoutePlanCandidateRejectionReasonEnum.CommuteTooLong:
      return 'för lång pendling';
    case RoutePlanCandidateRejectionReasonEnum.CannotReachExistingAssignment:
      return 'hinner inte till sitt nästa uppdrag';
    default:
      return other.fitsSchedule ? 'längre bort' : 'schemat räcker inte';
  }
};

/**
 * Omräkningen efter ett ombokningsja bär bara den valda fälttestaren — servern kör inte om
 * fördelningen för ett enda paket. Är det samma fälttestare och samma första upphämtning står
 * jämförelsen sig (närheten mäts dit), så den följer med. Annars gäller den inte längre.
 */
export const keepCandidateChoice = (
  evaluated: RoutePackageDTO,
  previous: RoutePackageDTO
): RoutePackageDTO => {
  const previousTop = previous.candidates.find((c) => c.rank === 1);
  const evaluatedTop = evaluated.candidates.find((c) => c.rank === 1);
  if (
    !previousTop ||
    !evaluatedTop ||
    previousTop.userId !== evaluatedTop.userId ||
    previous.legs[0]?.assignmentId !== evaluated.legs[0]?.assignmentId
  ) {
    return evaluated;
  }

  return {
    ...evaluated,
    candidateChoice: previous.candidateChoice,
    otherFieldTesters: previous.otherFieldTesters,
    candidates: [
      {
        ...evaluatedTop,
        proximityKm: previousTop.proximityKm,
        isGlobalAssignment: previousTop.isGlobalAssignment,
        gotFullTimeFirst: previousTop.gotFullTimeFirst,
      },
      ...previous.candidates.filter((c) => c.rank !== 1),
    ],
  };
};

/**
 * Bortfallet per orsak, utan dem som jämförelsen visar som lediga nu: de var upptagna när
 * paketet fördelades men är inte förstaval någonstans i förslaget. Annars står samma person
 * både som "ledig nu" i tabellen och som "redan bokad" i bortfallet under den.
 */
export const rejectionsShownWithChoice = (
  pkg: RoutePackageDTO
): RoutePackageDTO['candidateRejections'] => {
  const freedNow = (pkg.otherFieldTesters ?? []).filter(
    (o) =>
      o.reason === RoutePlanCandidateRejectionReasonEnum.AlreadyUsedInRun &&
      o.rankOneInPackageIndex == null
  ).length;
  if (freedNow === 0) return pkg.candidateRejections;
  return pkg.candidateRejections.map((r) =>
    r.reason === RoutePlanCandidateRejectionReasonEnum.AlreadyUsedInRun
      ? { ...r, count: Math.max(0, r.count - freedNow) }
      : r
  );
};
